import { useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { findProvince, TR_PROVINCES } from '@zt/content/tr-address';
import { Button, Dialog, EmptyState, Field, Input, Select, Spinner } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { useSalesRegions, useSalesUsers } from '../components/DealerSales';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { AutoAssignResult, SalesRegion } from '../lib/types';

type Place = { city: string; district: string | null };

const placeLabel = (p: Place) => (p.district ? `${p.city} / ${p.district}` : p.city);

/**
 * Satış bölgeleri (F7-08, ADR-111): il/ilçe listesi ve bölge sorumlusu. Sorumlu, bayi kapsamı "yalnızca atanan
 * bayiler" ise bölgesindeki bayileri görür. Bayiler il/ilçesine göre topluca bölgeye atanabilir (önizlemeyle).
 */
export function RegionsPage() {
  const t = useT();
  const { can, dealerScoped } = usePermissions();
  const manage = can('b2b.sales.manage') && !dealerScoped;
  const regions = useSalesRegions();
  const [editing, setEditing] = useState<SalesRegion | 'new' | null>(null);
  const [assigning, setAssigning] = useState(false);
  const remove = useApiMutation((id: string) => api.del(`/v1/admin/sales-regions/${id}`), {
    invalidate: [['sales-regions'], ['companies'], ['company']],
    success: t('admin.sales.regionDeleted'),
  });
  return (
    <PageFrame
      helpKey="page.sales_regions"
      actions={
        manage && (
          <>
            <div className="flex items-center gap-1">
              <Button onClick={() => setAssigning(true)}>{t('admin.sales.autoAssign')}</Button>
              <HelpTip k="sales_regions.auto_assign" />
            </div>
            <div className="flex items-center gap-1">
              <Button variant="primary" onClick={() => setEditing('new')}>
                <Plus className="h-4 w-4" aria-hidden />
                {t('admin.sales.newRegion')}
              </Button>
              <HelpTip k="sales_regions.new" />
            </div>
          </>
        )
      }
    >
      {regions.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : !regions.data?.length ? (
        <EmptyState title={t('admin.sales.emptyRegions')} description={t('admin.sales.emptyRegionsLead')} />
      ) : (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full min-w-[36rem] text-sm" aria-label={t('admin.sales.regionsTable')}>
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="px-3 py-2 text-start font-medium">{t('admin.sales.regionName')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.sales.places')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.sales.manager')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.sales.dealerCount')}</th>
                {manage && <th className="px-3 py-2" />}
              </tr>
            </thead>
            <tbody>
              {regions.data.map((r) => (
                <tr key={r.id} className="border-t border-line">
                  <td className="px-3 py-2 font-medium">{r.name}</td>
                  <td className="px-3 py-2 text-ink-2">
                    {r.places.length
                      ? r.places.slice(0, 6).map(placeLabel).join(', ') +
                        (r.places.length > 6
                          ? ` ${t('admin.sales.morePlaces', { count: r.places.length - 6 })}`
                          : '')
                      : '—'}
                  </td>
                  <td className="px-3 py-2">{r.managerName ?? t('admin.sales.noManager')}</td>
                  <td className="px-3 py-2 text-end tabular">{r.companyCount}</td>
                  {manage && (
                    <td className="px-3 py-2 text-end">
                      <span className="inline-flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>
                          {t('common.edit')}
                        </Button>
                        <ConfirmButton
                          message={t('admin.sales.confirmDelete', { name: r.name, count: r.companyCount })}
                          onConfirm={() => remove.mutateAsync(r.id)}
                        >
                          {t('common.delete')}
                        </ConfirmButton>
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <RegionDialog region={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
      {assigning && <AutoAssignDialog onClose={() => setAssigning(false)} />}
    </PageFrame>
  );
}

function RegionDialog({ region, onClose }: { region: SalesRegion | null; onClose: () => void }) {
  const t = useT();
  const users = useSalesUsers();
  const [name, setName] = useState(region?.name ?? '');
  const [manager, setManager] = useState(region?.managerUserId ?? '');
  const [places, setPlaces] = useState<Place[]>(region?.places ?? []);
  const [city, setCity] = useState('');
  const [district, setDistrict] = useState('');
  const province = findProvince(city);
  const save = useApiMutation(
    () => {
      const body = { name: name.trim(), places, managerUserId: manager || null };
      return region
        ? api.patch(`/v1/admin/sales-regions/${region.id}`, body, { ifMatch: region.version })
        : api.post('/v1/admin/sales-regions', body);
    },
    {
      invalidate: [['sales-regions']],
      success: t('admin.sales.regionSaved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  const add = () => {
    if (!province) return;
    const p = { city: province.name, district: district || null };
    if (!places.some((x) => x.city === p.city && x.district === p.district)) setPlaces([...places, p]);
    setDistrict('');
  };
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={region ? t('admin.sales.editRegion', { name: region.name }) : t('admin.sales.newRegion')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={name.trim().length < 2}
            onClick={() => save.mutate(undefined)}
          >
            {t('admin.sales.saveRegion')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('admin.sales.regionName')} required error={fieldError(save.error, 'name')}>
          {(p) => <Input {...p} maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field
          label={t('admin.sales.manager')}
          optionalLabel={t('common.optional')}
          help={<HelpTip k="sales_regions.manager" />}
          error={fieldError(save.error, 'managerUserId')}
        >
          {(p) => (
            <Select {...p} value={manager} onChange={(e) => setManager(e.target.value)}>
              <option value="">{t('admin.sales.noManager')}</option>
              {users.data?.map((u) => (
                <option key={u.userId} value={u.userId}>
                  {u.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <fieldset className="space-y-2">
          <legend className="flex items-center gap-1 text-sm font-medium">
            {t('admin.sales.places')} <HelpTip k="sales_regions.places" />
          </legend>
          <div className="flex flex-wrap items-end gap-2">
            <Field label={t('admin.sales.city')}>
              {(p) => (
                <Select
                  {...p}
                  value={province?.name ?? ''}
                  onChange={(e) => {
                    setCity(e.target.value);
                    setDistrict('');
                  }}
                >
                  <option value="">{t('admin.orders.chooseProvince')}</option>
                  {TR_PROVINCES.map((x) => (
                    <option key={x.code} value={x.name}>
                      {x.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={t('admin.sales.district')} optionalLabel={t('admin.sales.wholeCity')}>
              {(p) => (
                <Select
                  {...p}
                  value={district}
                  disabled={!province}
                  onChange={(e) => setDistrict(e.target.value)}
                >
                  <option value="">{t('admin.sales.wholeCity')}</option>
                  {province?.districts.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Button onClick={add} disabled={!province}>
              {t('admin.sales.addPlace')}
            </Button>
          </div>
          {places.length ? (
            <ul className="flex flex-wrap gap-2" aria-label={t('admin.sales.chosenPlaces')}>
              {places.map((p) => (
                <li
                  key={placeLabel(p)}
                  className="flex items-center gap-1 rounded-full border border-line bg-surface-2 px-3 py-1 text-sm"
                >
                  {placeLabel(p)}
                  <button
                    type="button"
                    className="text-ink-3 hover:text-rose"
                    aria-label={t('admin.sales.removePlace', { place: placeLabel(p) })}
                    onClick={() => setPlaces(places.filter((x) => x !== p))}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-3">{t('admin.sales.noPlaces')}</p>
          )}
          {save.error?.errors.some((e) => e.field.startsWith('places')) && (
            <p role="alert" className="text-sm text-rose">
              {save.error.title}
            </p>
          )}
        </fieldset>
      </div>
    </Dialog>
  );
}

function AutoAssignDialog({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [all, setAll] = useState(false);
  const [preview, setPreview] = useState<AutoAssignResult | null>(null);
  const run = useApiMutation(
    (dryRun: boolean) =>
      api.post<AutoAssignResult>('/v1/admin/sales-regions/auto-assign', { onlyUnassigned: !all, dryRun }),
    {
      invalidate: [['sales-regions'], ['companies'], ['company']],
      onSuccess: (r) => {
        if (r.applied) {
          toast.success(t('admin.sales.autoAssigned', { count: r.assigned }));
          onClose();
        } else setPreview(r);
      },
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('admin.sales.autoAssign')}
      description={t('admin.sales.autoAssignLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          {preview ? (
            <Button
              variant="primary"
              loading={run.isPending}
              disabled={!preview.assigned}
              onClick={() => run.mutate(false)}
            >
              {t('admin.sales.applyAssign', { count: preview.assigned })}
            </Button>
          ) : (
            <Button variant="primary" loading={run.isPending} onClick={() => run.mutate(true)}>
              {t('admin.sales.previewAssign')}
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-3">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="accent-[var(--zt-pine)]"
            checked={all}
            onChange={(e) => {
              setAll(e.target.checked);
              setPreview(null);
            }}
          />
          {t('admin.sales.reassignAll')}
        </label>
        {preview && (
          <div role="status" className="space-y-2 text-sm">
            <p>
              {t('admin.sales.previewSummary', { assigned: preview.assigned, unmatched: preview.unmatched })}
            </p>
            {preview.preview.length > 0 && (
              <ul className="list-disc ps-5 text-ink-2">
                {preview.preview.map((m) => (
                  <li key={m.companyId}>
                    {m.legalName} → {m.regionName}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
}
